"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isReservedThemeName = isReservedThemeName;
exports.reservedThemeNames = reservedThemeNames;
exports.validateThemeManifest = validateThemeManifest;
exports.registerTheme = registerTheme;
exports.unregisterTheme = unregisterTheme;
exports.setThemeListed = setThemeListed;
/**
 * 主题校验 + 运行时注册表。
 *
 * 为什么要有这一层：主题原本是 `tokens.ts` 里的四套常量，跟着代码一起发版，
 * 天然不会出错。上了"超管在后台发布主题"之后，主题变成**外部输入**——
 * 一份缺了两条语义令牌、或者文字与底色对比度只有 2:1 的 manifest 也能进来，
 * 而它的症状是"客户换成这套主题后，标题看不见了"。
 *
 * 所以上传的每一份 manifest 都必须先过这里：
 *
 *   1) 结构：name / version / label / description / tokens / variants
 *   2) 令牌完备：与内置主题解析出来的变量集合逐条比对，缺哪条说哪条
 *   3) 对比度：正文、次要文字、品牌按钮、页头四对至少要 4.5:1
 *   4) 骨架原型：封闭枚举，写错不报错只会静默变回 split
 *   5) 价格：落在档位带里，避免"¥1/月"这种把市场搅乱的定价
 *
 * 校验**只返回问题清单**，不抛异常：发布页要把问题一次列全，而不是让人
 * 改一条、点一次发布、再看到下一条。
 */
const contrast_1 = require("./contrast");
const catalog_1 = require("./catalog");
const layout_1 = require("./layout");
const slots_1 = require("./slots");
const resolve_1 = require("./resolve");
const tokens_1 = require("./tokens");
const pricing_1 = require("./pricing");
const ARCHETYPES = ['split', 'editorial', 'showcase'];
/** 与 scripts/theme/validate.ts 同一套判据，避免"命令行过了、后台上传不过" */
const MIN_CONTRAST = 4.5;
const MAX_MANIFEST_BYTES = 160_000;
const NAME_RE = /^[a-z][a-z0-9-]{2,23}$/;
/**
 * 内置主题的名字是保留字。
 *
 * 允许覆盖的话，一次误传就能把 aurora 换成任意一份 manifest，
 * 而 aurora 是所有客户降级时的落点 —— 这个口子不能开。
 *
 * **必须在模块加载时快照**，不能每次去 `THEMES` 上判 own property：
 * 运行时发布进来的主题同样是 own property，那样"下架"会被自己挡住
 * （`unregisterTheme` 先判 reserved 再删，判成保留就等于永远删不掉）。
 * 这个坑在单测里踩到过一次，症状是点下架返回 400 而主题还在市场里。
 */
const RESERVED_THEME_NAMES = new Set(Object.keys(tokens_1.THEMES));
function isReservedThemeName(name) {
    return RESERVED_THEME_NAMES.has(name);
}
/** 内置主题名。发布页用它提示"这个名字被占用了" */
function reservedThemeNames() {
    return [...RESERVED_THEME_NAMES];
}
/**
 * 必备令牌 = 内置主题（aurora）解析出来的那批变量。
 *
 * 不在这里硬编码 51 个变量名：aurora 是事实上的基线，它加了新令牌，
 * 校验自动跟着要求；硬编码的话两边会慢慢漂移，而漂移的症状是新主题
 * 缺一条变量 → 渲染时 `var()` 落空 → 某处颜色变成透明。
 */
function requiredVars() {
    const baseline = tokens_1.THEMES['aurora'];
    if (!baseline)
        return [];
    return Object.keys((0, resolve_1.resolveTheme)({ manifest: baseline }).vars);
}
function asRecord(value) {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value
        : null;
}
function nonEmptyText(value) {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}
/** 预览图地址的长度上限。够放下带签名的 COS 地址，又不至于让 manifest 变成藏 URL 的地方 */
const PREVIEW_URL_MAX = 400;
function previewUrlProblem(value) {
    if (typeof value !== 'string' || !value.trim())
        return '必须是地址字符串';
    const url = value.trim();
    if (url.length > PREVIEW_URL_MAX)
        return `超过 ${PREVIEW_URL_MAX} 个字符`;
    if (!/^https:\/\//i.test(url))
        return '必须是 https 地址（http 在 https 页面上会被浏览器拦掉）';
    return null;
}
function validateThemeManifest(input) {
    const errors = [];
    const warnings = [];
    let tokenCount = 0;
    const push = (code, message) => {
        errors.push({ code, message });
    };
    const root = asRecord(input);
    if (!root) {
        return {
            ok: false,
            errors: [{ code: 'not_object', message: '主题包必须是一个 JSON 对象' }],
            warnings,
            tokenCount: 0,
        };
    }
    // ── 体积 ────────────────────────────────────────────────────────
    const bytes = Buffer.byteLength(JSON.stringify(root), 'utf8');
    if (bytes > MAX_MANIFEST_BYTES) {
        push('too_large', `主题包 ${Math.round(bytes / 1024)}KB，超过上限 ${MAX_MANIFEST_BYTES / 1024}KB`);
    }
    // ── 标识 ────────────────────────────────────────────────────────
    const name = nonEmptyText(root['name']);
    if (!name) {
        push('missing_name', '缺少 name（主题的唯一标识，小写字母加连字符）');
    }
    else if (!NAME_RE.test(name)) {
        push('bad_name', `name「${name}」不符合规范：小写字母开头，只含小写字母、数字、连字符，3–24 位`);
    }
    else if (isReservedThemeName(name)) {
        push('reserved_name', `「${name}」是内置主题的名字，不能占用`);
    }
    const version = nonEmptyText(root['version']);
    if (!version) {
        push('missing_version', '缺少 version（形如 1.0.0）');
    }
    else if (!/^\d+\.\d+\.\d+$/.test(version)) {
        push('bad_version', `version「${version}」不是 主.次.补丁 格式`);
    }
    for (const field of ['label', 'description']) {
        const value = asRecord(root[field]);
        if (!value) {
            push(`missing_${field}`, `缺少 ${field}（zh / en 两份文案）`);
            continue;
        }
        for (const locale of ['zh', 'en']) {
            if (!nonEmptyText(value[locale])) {
                push(`missing_${field}_${locale}`, `${field}.${locale} 不能为空`);
            }
        }
    }
    // ── 市场预览图 ──────────────────────────────────────────────────
    // 只查形态，不查归属：这一层被站点、控制台、浏览器共用，它不知道我们的
    // 桶叫什么。「这张图是不是我们桶里的」由平台侧发布接口核对。
    // 但形态必须卡：一个 `javascript:` 地址进了 manifest，控制台用 <img src>
    // 渲染时就是一次注入；而一个 http 地址会在 https 页面上被静默拦掉。
    if (root['preview'] !== undefined) {
        const preview = asRecord(root['preview']);
        if (!preview) {
            push('bad_preview', 'preview 必须是对象：{ card: 卡片图地址, hero: 详情大图地址 }');
        }
        else {
            for (const kind of ['card', 'hero']) {
                const url = preview[kind];
                if (url === undefined || url === null)
                    continue;
                const problem = previewUrlProblem(url);
                if (problem)
                    push(`bad_preview_${kind}`, `preview.${kind} ${problem}`);
            }
        }
    }
    // ── 骨架原型 ────────────────────────────────────────────────────
    const archetype = root['archetype'];
    if (archetype === undefined) {
        warnings.push({
            code: 'default_archetype',
            message: '没写 archetype，将按 split（左文右图）渲染',
        });
    }
    else if (!ARCHETYPES.includes(archetype)) {
        push('bad_archetype', `archetype 只能是 ${ARCHETYPES.join(' / ')}，收到「${String(archetype)}」`);
    }
    // ── 是否陈列 ────────────────────────────────────────────────────
    /**
     * 只判类型。`listed` 是"这套主题摆不摆到市场上"，写错类型的后果是静默的：
     * 字符串 `"false"` 在 `manifest.listed !== false` 里算 true —— 一套本来
     * 想撤下的主题会继续在市场里卖，而发布页不会报错。
     */
    const listed = root['listed'];
    if (listed !== undefined && typeof listed !== 'boolean') {
        push('bad_listed', `listed 只能是 true / false，收到「${String(listed)}」`);
    }
    // ── 定价 ────────────────────────────────────────────────────────
    const pricing = asRecord(root['pricing']);
    if (pricing) {
        const cents = pricing['monthlyCents'];
        if (typeof cents !== 'number' || !Number.isInteger(cents) || cents < 0) {
            push('bad_price', 'pricing.monthlyCents 必须是非负整数（单位：分）');
        }
        else if (cents > 0) {
            /**
             * 只判"落不落在某个档位带里"，不判档位名。
             *
             * 档位（plus / premium）是**我们内部用来分价格带的**，主题包里不必写；
             * 而带与带之间刻意留了缝（¥49~¥69），落进缝里说明定价抄错了 ——
             * 按缝判能在发布那一刻拦住，而不是等客户在市场上看到 ¥59 这个怪价。
             */
            const tier = (0, pricing_1.tierOfPrice)(cents);
            if (!tier) {
                const fmt = (v) => `¥${(v / 100).toFixed(2).replace(/\.00$/, '')}`;
                const bands = pricing_1.THEME_TIERS.map((t) => `${fmt(pricing_1.THEME_TIER_PRICE_BAND[t].min)}–${fmt(pricing_1.THEME_TIER_PRICE_BAND[t].max)}`).join('、');
                push('price_out_of_band', `月价 ${fmt(cents)} 不在任何档位区间（${bands}）`);
            }
        }
        else {
            warnings.push({
                code: 'free_theme',
                message: 'monthlyCents 为 0：这套主题会被当成免费主题，不受订阅限制',
            });
        }
    }
    else {
        warnings.push({
            code: 'no_pricing',
            message: '没写 pricing：这套主题会出现在「基础主题」里、对所有人免费',
        });
    }
    // ── 令牌 ────────────────────────────────────────────────────────
    const tokens = asRecord(root['tokens']);
    if (!tokens) {
        push('missing_tokens', '缺少 tokens（primitive / semantic 两棵树）');
    }
    else {
        for (const layer of ['primitive', 'semantic']) {
            if (!asRecord(tokens[layer]))
                push(`missing_tokens_${layer}`, `缺少 tokens.${layer}`);
        }
    }
    // ── 变体 ────────────────────────────────────────────────────────
    const variants = asRecord(root['variants']);
    if (!variants || Object.keys(variants).length === 0) {
        push('missing_variants', '至少需要一个变体（variants.default）');
    }
    else {
        for (const [key, raw] of Object.entries(variants)) {
            const variant = asRecord(raw);
            if (!variant) {
                push('bad_variant', `变体「${key}」不是对象`);
                continue;
            }
            if (variant['key'] !== key) {
                push('variant_key_mismatch', `变体「${key}」里的 key 字段必须与键名一致`);
            }
            const label = asRecord(variant['label']);
            if (!label || !nonEmptyText(label['zh']) || !nonEmptyText(label['en'])) {
                push('variant_missing_label', `变体「${key}」缺少 label（zh / en）`);
            }
            if (!asRecord(variant['tokens'])) {
                push('variant_missing_tokens', `变体「${key}」缺少 tokens`);
            }
        }
    }
    // ── 模板层 ──────────────────────────────────────────────────────
    /**
     * 只判形状与段名。组件是否真的存在由命令行/后台查组件注册表 ——
     * theme-kit 不依赖 @icetus/blocks（依赖方向是 blocks → contracts），
     * 在这里反向引用会形成环，而环在这种"到处被 import"的包上极难拆。
     *
     * 段名校验（`slots.ts`）是**发布前的最后一道**：`instance_id` 是客户内容
     * 跨主题的地址，写成 `feature-1` 也能渲染，只是客户换主题那天才发现
     * 整站的字不见了 —— 那时已经没有任何报错能指向这里。
     */
    const layout = root['layout'];
    if (layout !== undefined) {
        for (const issue of (0, layout_1.validateLayoutShape)(layout)) {
            push('bad_layout', issue.message);
        }
        const slotCheck = (0, slots_1.validateLayoutSlots)(layout);
        for (const issue of slotCheck.errors) {
            push('bad_layout_slot', issue.message);
        }
        for (const issue of slotCheck.warnings) {
            warnings.push({ code: 'layout_slot', message: issue.message });
        }
    }
    const componentStyles = root['componentStyles'];
    if (componentStyles !== undefined) {
        if (componentStyles === null || typeof componentStyles !== 'object') {
            push('bad_component_styles', 'componentStyles 必须是对象');
        }
        else {
            for (const [component, slots] of Object.entries(componentStyles)) {
                if (slots === null || typeof slots !== 'object') {
                    push('bad_component_styles', `componentStyles.${component} 必须是对象`);
                    continue;
                }
                for (const [slot, value] of Object.entries(slots)) {
                    if (typeof value !== 'string' && typeof value !== 'number') {
                        push('bad_component_styles', `componentStyles.${component}.${slot} 只能是字符串或数字`);
                    }
                }
            }
        }
    }
    // 结构性问题已经够多时不再往下解析：resolveTheme 对残缺 manifest 会抛
    if (errors.length > 0) {
        return { ok: false, errors, warnings, tokenCount };
    }
    // ── 令牌完备 + 对比度 ───────────────────────────────────────────
    const manifest = root;
    let resolved;
    try {
        resolved = (0, resolve_1.resolveTheme)({ manifest });
    }
    catch (err) {
        push('resolve_failed', `令牌解析失败：${err instanceof Error ? err.message : String(err)}`);
        return { ok: false, errors, warnings, tokenCount };
    }
    tokenCount = Object.keys(resolved.vars).length;
    const required = requiredVars();
    const missing = required.filter((key) => !(key in resolved.vars));
    if (missing.length > 0) {
        // 只列前 8 条：一次列 30 条没人看得下去，其余用数量带过
        const head = missing.slice(0, 8).join('、');
        push('missing_tokens', `缺少 ${missing.length} 条语义令牌：${head}${missing.length > 8 ? ' 等' : ''}`);
    }
    const pairs = [
        { label: '正文 / 页面底色', fg: '--color-text', bg: '--color-bg', code: 'contrast_text' },
        {
            label: '次要文字 / 页面底色',
            fg: '--color-text-muted',
            bg: '--color-bg',
            code: 'contrast_muted',
        },
        { label: '按钮文字 / 品牌色', fg: '--color-on-brand', bg: '--color-brand', code: 'contrast_brand' },
        {
            label: '页头文字 / 页头底色',
            fg: '--color-header-text',
            bg: '--color-header-bg',
            code: 'contrast_header',
        },
    ];
    for (const pair of pairs) {
        const fg = resolved.vars[pair.fg];
        const bg = resolved.vars[pair.bg];
        if (!fg || !bg)
            continue; // 缺令牌的问题已经报过一次，不重复
        const ratio = (0, contrast_1.contrastRatio)(fg, bg);
        if (ratio === null) {
            warnings.push({
                code: `${pair.code}_unparsed`,
                message: `${pair.label}：色值不是十六进制（${fg} / ${bg}），无法自动检查对比度`,
            });
            continue;
        }
        if (ratio < MIN_CONTRAST) {
            push(`${pair.code}_low`, `${pair.label} 对比度 ${ratio.toFixed(2)}:1，低于 ${MIN_CONTRAST}:1（${fg} on ${bg}）`);
        }
    }
    // 变体里的字号/间距必须有值，否则某些排版在窄屏上会挤成一团
    const flat = (0, resolve_1.flattenTokens)(manifest.tokens.semantic);
    if (Object.keys(flat).length === 0) {
        push('empty_semantic', 'tokens.semantic 是空的');
    }
    return { ok: errors.length === 0, errors, warnings, tokenCount };
}
/**
 * 把一份已校验的 manifest 注册进运行时表。
 *
 * 只在这里写 THEMES：控制台市场页、平台保存校验、站点渲染读的都是同一个
 * 对象，任何别处"顺手 set 一下"都会让三处看到不同的主题集合。
 */
function registerTheme(manifest) {
    if (isReservedThemeName(manifest.name)) {
        throw new Error(`不能覆盖内置主题：${manifest.name}`);
    }
    tokens_1.THEMES[manifest.name] = manifest;
    (0, catalog_1.invalidateThemeSummaries)();
}
/** 下线一套运行时主题。内置主题删不掉（调用方先判 reserved） */
function unregisterTheme(name) {
    if (isReservedThemeName(name))
        return false;
    const existed = Boolean(tokens_1.THEMES[name]);
    delete tokens_1.THEMES[name];
    if (existed)
        (0, catalog_1.invalidateThemeSummaries)();
    return existed;
}
/**
 * 改一套主题的"是否在市场陈列"。
 *
 * 为什么不是 `delete THEMES[name]`：删掉以后 `theme_subscriptions` 里的行
 * 就指向一个解析不到的主题，续费中的客户下次请求会静默掉回 aurora，
 * 而他付的钱还没到期。下架只影响**能不能买**，不影响**能不能用**。
 *
 * 生成新对象而不是就地改：调用方（仓储读出来的 manifest、内置常量）
 * 可能持有同一个引用，就地改会让"库里是 published、内存里是 listed:false"
 * 这种状态在两处不一致。
 */
function setThemeListed(name, listed) {
    const manifest = tokens_1.THEMES[name];
    if (!manifest)
        return false;
    if ((manifest.listed !== false) === listed)
        return true;
    tokens_1.THEMES[name] = { ...manifest, listed };
    (0, catalog_1.invalidateThemeSummaries)();
    return true;
}
//# sourceMappingURL=validate.js.map